import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { MarginProperties } from '@iheartjane/reefer';
import {
  Box,
  Button,
  Flex,
  TrashIcon,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { useCallback } from 'react';

import { useDeleteMenuRow } from '@jane/business-admin/data-access';
import type { EmbeddedMenuRow, MenuRow } from '@jane/business-admin/types';

import { EmbedCodeButton } from './ListItemHeaders';
import { PreviewCardsListItem } from './PreviewCardsListItem';
import styles from './landing.module.css';

const GrayBubble = ({
  enabled,
  height = 12,
  width = 72,
  ...props
}: {
  enabled?: boolean;
  height?: number;
  width?: number;
} & MarginProperties) => (
  <Box
    className={styles['grayBubble']}
    height={height}
    width={width}
    background={enabled ? 'grays-light' : 'grays-ultralight'}
    {...props}
  />
);

const CardText = ({ enabled }: { enabled?: boolean }) => (
  <Flex
    flexDirection="column"
    alignItems="left"
    width="100%"
    height="100%"
    p={16}
  >
    <GrayBubble width={42} mb={4} enabled={enabled} />
    <Typography
      variant="body-bold"
      color={enabled ? 'text-main' : 'grays-light'}
    >
      AI-curated product
    </Typography>
    <Typography color={enabled ? 'grays-mid' : 'grays-light'}>
      Updated daily
    </Typography>
    <GrayBubble mt={4} enabled={enabled} />
    <GrayBubble width={88} mt={4} enabled={enabled} />
    <GrayBubble height={16} mt="auto" ml="auto" enabled={enabled} />
  </Flex>
);

export const RecommendedRowListItem = ({
  embeddedMenuRow,
  menuRow,
  openEmbedRowModal,
  rowHelperText,
  showEmbedButton,
  storeId,
}: {
  embeddedMenuRow?: EmbeddedMenuRow | null;
  menuRow: MenuRow;
  openEmbedRowModal: (menuRow: MenuRow, row?: EmbeddedMenuRow | null) => void;
  rowHelperText?: string;
  showEmbedButton: boolean;
  storeId: string;
}) => {
  const thematicRows = useFlag(FLAGS.comm3MultipleThematicRows);
  const toast = useToast();
  const { mutate: deleteMenuRow } = useDeleteMenuRow(storeId, menuRow.id || 0);

  const handleDeleteRow = useCallback(() => {
    if (menuRow.id) {
      deleteMenuRow(undefined, {
        onSuccess: () => {
          toast.add({
            label: 'Recommended row deleted successfully',
            variant: 'success',
          });
        },
        onError: () => {
          toast.add({
            label: 'Failed to delete recommended row',
            variant: 'error',
          });
        },
      });
    }
  }, [menuRow.id, deleteMenuRow, toast]);

  return (
    <PreviewCardsListItem
      afterHeader={
        thematicRows || showEmbedButton ? (
          <Flex alignItems="center" gap={24}>
            {!thematicRows && showEmbedButton && (
              <EmbedCodeButton
                hasEmbedCode={!!embeddedMenuRow}
                onClick={() => openEmbedRowModal(menuRow, embeddedMenuRow)}
              />
            )}
            {thematicRows && (
              <>
                <Typography color="gradient-primary">
                  AI-Powered suggested products
                </Typography>
                {showEmbedButton && (
                  <EmbedCodeButton
                    hasEmbedCode={!!embeddedMenuRow}
                    onClick={() => openEmbedRowModal(menuRow, embeddedMenuRow)}
                  />
                )}
                <Button.Icon
                  ariaLabel="Delete recommended row"
                  variant="tertiary"
                  icon={<TrashIcon />}
                  onClick={handleDeleteRow}
                />
              </>
            )}
          </Flex>
        ) : undefined
      }
      cardText={
        thematicRows ? (
          <CardText enabled={menuRow.enabled} />
        ) : (
          'Recommended product'
        )
      }
      row={menuRow}
      rowHelperText={
        thematicRows ? (
          <Typography as="span" variant="body" color="grays-black">
            {rowHelperText}{' '}
            <Typography as="span" variant="mini" color="grays-mid">
              (Example row)
            </Typography>
          </Typography>
        ) : (
          'Each customer will see personalized products tailored to their unique tastes and preferences.'
        )
      }
    >
      {thematicRows && (
        <Typography
          variant="body"
          color="grays-mid"
          textAlign="center"
          width="100%"
          mb={40}
          mt={-32}
        >
          Each row highlights a unique product group tailored to your customers'
          preferences.
        </Typography>
      )}
    </PreviewCardsListItem>
  );
};
